import React, { useState, useContext, useMemo, useEffect } from "react";
import debounce from "lodash/debounce";
import { Button } from "@jem-open/jem-ui";
import { showNotification } from "@mantine/notifications";
import { Plus, Filter, FileText } from "lucide-react";
import PageContainer from "../../../themes/components/PageContainer";
import { AuthContext } from "../../../providers/AuthProvider";

import {
  useMcmAnnouncementListApiService,
  useMcmAnnouncementDetailApiService,
  useMcmAnnouncementTypeListApiService,
} from "../services/api/McmAnnouncementQueryApiService";
import {
  useCreateMcmAnnouncementApiService,
  useUpdateMcmAnnouncementApiService,
  useDeleteMcmAnnouncementApiService,
  usePublishMcmAnnouncementApiService,
  useCloseMcmAnnouncementApiService,
} from "../services/api/McmAnnouncementMutationApiService";
import {
  useAllMcmSegmentsQuery,
  useCreateMcmSegmentApiService,
  useUpdateMcmSegmentApiService,
  useDeleteMcmSegmentApiService,
} from "../services/api/McmSegmentApiService";
import {
  useCompanyAffiliationListApiService,
  useMcmAvailableFieldsApiService,
  useMcmAuditLogApiService,
} from "../services/api/McmSupportApiService";
import useSegmentListApiService from "../../segments/services/api/SegmentListApiService";

import MessageTable from "../components/MessageTable";
import ComposeForm from "../components/ComposeForm";
import SegmentManagementPanel from "../components/SegmentManagementPanel";
import SegmentBuilder from "../components/SegmentBuilder";
import SegmentDetailView from "../components/SegmentDetailView";
import SegmentEmployeeList from "../components/SegmentEmployeeList";
import MessageDetailView from "../components/MessageDetailView";
import AuditLogPanel from "../components/AuditLogPanel";

import MultimessagingBreadcrumb from "../elements/MultimessagingBreadcrumb";
import DeleteDraftModal from "../elements/DeleteDraftModal";
import CloseAnnouncementModal from "../elements/CloseAnnouncementModal";

import CreateMcmAnnouncementRequest from "../services/request/CreateMcmAnnouncementRequest";

type View =
  | "list"
  | "compose"
  | "audit"
  | "segments"
  | "view"
  | "create-segment"
  | "edit-segment"
  | "view-segment"
  | "segment-employees";

const MultimessagingDirectoryPage: React.FC = () => {
  const auth = useContext(AuthContext);

  // Navigation / modal state
  const [view, setView] = useState<View>("list");
  const [showCloseModal, setShowCloseModal] = useState(false);

  // Selection state (IDs instead of full objects)
  const [viewingAnnouncementId, setViewingAnnouncementId] = useState<
    string | null
  >(null);
  const [editingAnnouncementId, setEditingAnnouncementId] = useState<
    string | null
  >(null);
  const [deletingAnnouncementId, setDeletingAnnouncementId] = useState<
    string | null
  >(null);
  const [deletingAnnouncementTitle, setDeletingAnnouncementTitle] =
    useState("");
  const [viewingSegmentId, setViewingSegmentId] = useState<number | null>(null);
  const [editingSegmentId, setEditingSegmentId] = useState<number | null>(null);
  const [segmentTargetCompanyId, setSegmentTargetCompanyId] = useState<
    number | null
  >(null);

  // Pagination, search & sort state
  const [announcementPage, setAnnouncementPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [ordering, setOrdering] = useState("-date_created");
  const [auditPage, setAuditPage] = useState(1);

  const applyDebouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearch(value);
        setAnnouncementPage(1);
      }, 400),
    []
  );

  useEffect(
    () => () => {
      applyDebouncedSearch.cancel();
    },
    [applyDebouncedSearch]
  );

  // Query hooks
  const companyListQuery = useCompanyAffiliationListApiService({
    page_size: 40,
  });
  const segmentListQuery = useAllMcmSegmentsQuery();
  // We intentionally do NOT request include_match_count here: that computation
  // scans the whole employee table once per segment and times out the API for
  // large employers (e.g. Servest: ~18k employees x 20 segments), leaving the
  // picker empty. The deduplicated total is fetched via the estimate endpoint
  // downstream; the per-segment count rendered alongside each segment row
  // gracefully falls back to "—" when absent. Same fix as v1.82.1 for the
  // EWA segmentation block drawer.
  const ownerSegmentQuery = useSegmentListApiService(
    { employer: auth?.employer?.id ?? 0 },
    !!auth?.employer?.id
  );
  const announcementListQuery = useMcmAnnouncementListApiService({
    page: announcementPage,
    page_size: 10,
    ordering,
    search: debouncedSearch || undefined,
  });

  const announcementTypesQuery = useMcmAnnouncementTypeListApiService();
  const availableFieldsQuery = useMcmAvailableFieldsApiService(
    {
      employer: auth?.employer?.id ?? 0,
      target_company: segmentTargetCompanyId ?? undefined,
    },
    Boolean(auth?.employer?.id && segmentTargetCompanyId)
  );
  const auditLogQuery = useMcmAuditLogApiService({ page: auditPage });
  const editingDetailQuery = useMcmAnnouncementDetailApiService(
    editingAnnouncementId
  );

  // Mutation hooks
  const deleteAnnouncementMutation = useDeleteMcmAnnouncementApiService();
  const closeAnnouncementMutation = useCloseMcmAnnouncementApiService();
  const createAnnouncementMutation = useCreateMcmAnnouncementApiService();
  const updateAnnouncementMutation = useUpdateMcmAnnouncementApiService();
  const publishAnnouncementMutation = usePublishMcmAnnouncementApiService();
  const createSegmentMutation = useCreateMcmSegmentApiService();
  const updateSegmentMutation = useUpdateMcmSegmentApiService();
  const deleteSegmentMutation = useDeleteMcmSegmentApiService();

  // Derived data
  const companies = companyListQuery.data || [];
  const segments = segmentListQuery.data || [];
  const announcements = announcementListQuery.data?.results || [];
  const announcementTypes = announcementTypesQuery.data || [];
  const availableFields = availableFieldsQuery.data || [];
  const isFieldsStale =
    availableFieldsQuery.isPreviousData && availableFieldsQuery.isFetching;
  const auditEntries = auditLogQuery.data?.results || [];
  const ownerSegments = (ownerSegmentQuery.data?.results || []).filter(
    (s) => s.is_active
  );
  const ownerCompanyId = auth?.employer?.id;
  const ownerCompanyName = auth?.employer?.company_name ?? "";

  const editingAnnouncement = editingAnnouncementId
    ? editingDetailQuery.data ||
      announcements.find((a) => a.id === editingAnnouncementId) ||
      null
    : null;
  const viewingSegment = viewingSegmentId
    ? segments.find((s) => s.id === viewingSegmentId) || null
    : null;
  const editingSegment = editingSegmentId
    ? segments.find((s) => s.id === editingSegmentId) || null
    : null;

  // Resolve target company name from companies list or from the editing segment
  const targetCompanyName = segmentTargetCompanyId
    ? companies.find((c) => c.target_employer.id === segmentTargetCompanyId)
        ?.target_employer.company_name ||
      editingSegment?.target_company?.company_name ||
      ""
    : "";

  // Helpers for closing announcement (from detail or compose view)
  const closingAnnouncementId = viewingAnnouncementId || editingAnnouncementId;

  return (
    <PageContainer>
      {/* Header */}
      <div
        className="flex items-center justify-between -mx-[40px] -mt-[32px] px-[40px] py-lg mb-md"
        style={{ backgroundColor: "rgba(237,244,251,0.5)" }}
      >
        <div>
          <h1 className="font-heading text-2xl font-semibold leading-36 text-greyscale-text-title">
            Multimessaging
          </h1>
          <p className="py-[6px] text-sm font-regular leading-18 text-greyscale-text-body">
            Send announcements across affiliated company profiles in a single
            action.
          </p>
        </div>
        <div className="flex items-center gap-xxs">
          {view === "list" && (
            <>
              <Button
                variant="outline"
                leftIcon={<Filter size={16} />}
                onClick={() => setView("segments")}
              >
                Manage Segments
              </Button>
              <Button
                variant="outline"
                leftIcon={<FileText size={16} />}
                onClick={() => setView("audit")}
              >
                History Log
              </Button>
              <Button
                leftIcon={<Plus size={16} />}
                onClick={() => {
                  setEditingAnnouncementId(null);
                  setView("compose");
                }}
              >
                Create New
              </Button>
            </>
          )}
        </div>
      </div>

      {/* ---------- LIST VIEW ---------- */}
      {view === "list" && (
        <MessageTable
          messages={announcements}
          searchQuery={searchInput}
          ordering={ordering}
          onSearchChange={(q) => {
            setSearchInput(q);
            applyDebouncedSearch(q);
          }}
          onSortChange={(o) => {
            setOrdering(o);
            setAnnouncementPage(1);
          }}
          isLoading={announcementListQuery.isLoading}
          page={announcementPage}
          totalPages={announcementListQuery.data?.pages || 1}
          totalCount={announcementListQuery.data?.count || 0}
          onPageChange={setAnnouncementPage}
          onView={(m) => {
            setViewingAnnouncementId(m.id);
            setView("view");
          }}
          onEdit={(m) => {
            setEditingAnnouncementId(m.id);
            setView("compose");
          }}
          onDelete={(m) => {
            setDeletingAnnouncementId(m.id);
            setDeletingAnnouncementTitle(m.title);
          }}
        />
      )}

      {/* ---------- COMPOSE VIEW ---------- */}
      {view === "compose" &&
        editingAnnouncementId &&
        editingDetailQuery.isLoading && (
          <div className="flex flex-col items-start gap-md py-lg">
            <p className="text-sm text-greyscale-text-caption">
              Loading draft...
            </p>
            <Button
              variant="outline"
              onClick={() => {
                setView("list");
                setEditingAnnouncementId(null);
              }}
            >
              Go Back
            </Button>
          </div>
        )}
      {view === "compose" &&
        (!editingAnnouncementId || !editingDetailQuery.isLoading) && (
          <ComposeForm
            companies={companies}
            segments={segments}
            ownerCompanyId={ownerCompanyId}
            ownerCompanyName={ownerCompanyName}
            ownerSegments={ownerSegments}
            announcementTypes={announcementTypes}
            editingAnnouncement={editingAnnouncement}
            isSaving={
              createAnnouncementMutation.isLoading ||
              updateAnnouncementMutation.isLoading
            }
            isPublishing={publishAnnouncementMutation.isLoading}
            onGoBack={() => {
              setView("list");
              setEditingAnnouncementId(null);
            }}
            onSaveDraft={(request: CreateMcmAnnouncementRequest) => {
              if (editingAnnouncementId) {
                updateAnnouncementMutation.mutate(
                  { id: editingAnnouncementId, ...request },
                  {
                    onSuccess: () => {
                      showNotification({
                        title: "Success",
                        message: "Draft saved successfully",
                        color: "green",
                      });
                      setEditingAnnouncementId(null);
                      setView("list");
                    },
                  }
                );
              } else {
                createAnnouncementMutation.mutate(request, {
                  onSuccess: () => {
                    showNotification({
                      title: "Success",
                      message: "Draft saved successfully",
                      color: "green",
                    });
                    setView("list");
                  },
                });
              }
            }}
            onPublish={(request: CreateMcmAnnouncementRequest) => {
              if (editingAnnouncementId) {
                updateAnnouncementMutation.mutate(
                  { id: editingAnnouncementId, ...request },
                  {
                    onSuccess: () => {
                      publishAnnouncementMutation.mutate(
                        editingAnnouncementId,
                        {
                          onSuccess: () => {
                            showNotification({
                              title: "Success",
                              message: "Announcement sent successfully",
                              color: "green",
                            });
                            setEditingAnnouncementId(null);
                            setView("list");
                          },
                          onError: () => {
                            // Draft was saved but publish failed — navigate to list
                            // so the user can retry from the saved draft.
                            setEditingAnnouncementId(null);
                            setView("list");
                          },
                        }
                      );
                    },
                  }
                );
              } else {
                createAnnouncementMutation.mutate(request, {
                  onSuccess: (created) => {
                    publishAnnouncementMutation.mutate(created.id, {
                      onSuccess: () => {
                        showNotification({
                          title: "Success",
                          message: "Announcement sent successfully",
                          color: "green",
                        });
                        setView("list");
                      },
                      onError: () => {
                        // Draft was created but publish failed — navigate to list
                        // to avoid duplicate creation on retry.
                        setView("list");
                      },
                    });
                  },
                });
              }
            }}
            onCloseAnnouncement={() => setShowCloseModal(true)}
          />
        )}

      {/* ---------- MANAGE SEGMENTS ---------- */}
      {view === "segments" && (
        <SegmentManagementPanel
          companies={companies}
          segments={segments}
          ownerCompanyName={ownerCompanyName}
          ownerSegments={ownerSegments}
          isLoading={segmentListQuery.isLoading || companyListQuery.isLoading}
          isDeleting={deleteSegmentMutation.isLoading}
          onCreateSegment={(companyId) => {
            setSegmentTargetCompanyId(companyId);
            setView("create-segment");
          }}
          onViewSegment={(seg) => {
            setViewingSegmentId(seg.id);
            setView("view-segment");
          }}
          onEditSegment={(seg) => {
            setEditingSegmentId(seg.id);
            setSegmentTargetCompanyId(seg.target_company?.id ?? null);
            setView("edit-segment");
          }}
          onDeleteSegment={(segmentId, onSuccess) => {
            deleteSegmentMutation.mutate(segmentId, { onSuccess });
          }}
          onViewEmployees={(seg) => {
            setViewingSegmentId(seg.id);
            setView("segment-employees");
          }}
          onGoBack={() => setView("list")}
        />
      )}

      {/* ---------- CREATE NEW SEGMENT ---------- */}
      {view === "create-segment" && segmentTargetCompanyId && (
        <div>
          <MultimessagingBreadcrumb
            items={[
              {
                label: "Manage Segments",
                onClick: () => {
                  setView("segments");
                  setSegmentTargetCompanyId(null);
                },
              },
              { label: `New Segment — ${targetCompanyName}` },
            ]}
          />

          <h2 className="text-xl font-heading font-semibold text-greyscale-text-title mb-lg">
            Create new segment
          </h2>

          <div className="max-w-[850px]">
            {(availableFieldsQuery.isLoading || isFieldsStale) && (
              <p className="text-sm text-greyscale-text-caption py-md">
                Loading available fields...
              </p>
            )}
            {!availableFieldsQuery.isLoading &&
              !isFieldsStale &&
              availableFieldsQuery.isError && (
                <p className="text-sm text-red-500 py-md">
                  Failed to load segment fields. Please try again.
                </p>
              )}
            {!availableFieldsQuery.isLoading &&
              !isFieldsStale &&
              !availableFieldsQuery.isError && (
                <SegmentBuilder
                  availableFields={availableFields}
                  targetCompanyId={segmentTargetCompanyId}
                  targetCompanyName={targetCompanyName}
                  isSaving={createSegmentMutation.isLoading}
                  onSave={(request) => {
                    createSegmentMutation.mutate(request, {
                      onSuccess: () => {
                        setSegmentTargetCompanyId(null);
                        setView("segments");
                      },
                    });
                  }}
                  onCancel={() => {
                    setSegmentTargetCompanyId(null);
                    setView("segments");
                  }}
                />
              )}
          </div>
        </div>
      )}

      {/* ---------- EDIT SEGMENT ---------- */}
      {view === "edit-segment" && editingSegment && segmentTargetCompanyId && (
        <div>
          <MultimessagingBreadcrumb
            items={[
              {
                label: "Manage Segments",
                onClick: () => {
                  setView("segments");
                  setEditingSegmentId(null);
                  setSegmentTargetCompanyId(null);
                },
              },
              { label: `Edit: ${editingSegment.name}` },
            ]}
          />

          <h2 className="text-xl font-heading font-semibold text-greyscale-text-title mb-lg">
            Edit segment
          </h2>

          <div className="max-w-[850px]">
            {(availableFieldsQuery.isLoading || isFieldsStale) && (
              <p className="text-sm text-greyscale-text-caption py-md">
                Loading available fields...
              </p>
            )}
            {!availableFieldsQuery.isLoading &&
              !isFieldsStale &&
              availableFieldsQuery.isError && (
                <p className="text-sm text-red-500 py-md">
                  Failed to load segment fields. Please try again.
                </p>
              )}
            {!availableFieldsQuery.isLoading &&
              !isFieldsStale &&
              !availableFieldsQuery.isError && (
                <SegmentBuilder
                  availableFields={availableFields}
                  targetCompanyId={segmentTargetCompanyId}
                  targetCompanyName={targetCompanyName}
                  defaultName={editingSegment.name}
                  defaultDescription={editingSegment.description}
                  defaultLogicOperator={editingSegment.logic_operator}
                  matchedEmployeeCount={editingSegment.matched_employee_count}
                  defaultRules={editingSegment.rules.map((r, i) => {
                    const fieldDef = availableFields.find(
                      (f) => f.field === r.field
                    );
                    let valueType: "text" | "date" | "select" = "text";
                    if (fieldDef?.type === "date") {
                      valueType = "date";
                    } else if (fieldDef?.options?.length) {
                      valueType = "select";
                    }
                    const serialized = String(r.value ?? "");

                    return {
                      localId: `edit-rule-${i}`,
                      field: r.field,
                      operator: r.operator,
                      values:
                        valueType === "text"
                          ? [serialized]
                          : serialized.split(",").map((v) => v.trim()),
                      valueType,
                    };
                  })}
                  saveLabel="Save Changes"
                  isSaving={updateSegmentMutation.isLoading}
                  onSave={(request) => {
                    updateSegmentMutation.mutate(
                      { id: editingSegment.id, ...request },
                      {
                        onSuccess: () => {
                          setEditingSegmentId(null);
                          setSegmentTargetCompanyId(null);
                          setView("segments");
                        },
                      }
                    );
                  }}
                  onCancel={() => {
                    setEditingSegmentId(null);
                    setSegmentTargetCompanyId(null);
                    setView("segments");
                  }}
                />
              )}
          </div>
        </div>
      )}

      {/* ---------- VIEW SEGMENT DETAIL ---------- */}
      {view === "view-segment" && viewingSegment && (
        <SegmentDetailView
          segment={viewingSegment}
          onGoBack={() => {
            setView("segments");
            setViewingSegmentId(null);
          }}
        />
      )}

      {/* ---------- SEGMENT EMPLOYEES LIST ---------- */}
      {view === "segment-employees" && viewingSegment && (
        <SegmentEmployeeList
          segment={viewingSegment}
          onGoBack={() => {
            setView("segments");
            setViewingSegmentId(null);
          }}
        />
      )}

      {/* ---------- VIEW MESSAGE DETAIL ---------- */}
      {view === "view" && viewingAnnouncementId && (
        <MessageDetailView
          announcementId={viewingAnnouncementId}
          onGoBack={() => {
            setView("list");
            setViewingAnnouncementId(null);
          }}
          onCloseAnnouncement={() => setShowCloseModal(true)}
        />
      )}

      {/* ---------- AUDIT LOG VIEW ---------- */}
      {view === "audit" && (
        <AuditLogPanel
          auditEntries={auditEntries}
          isLoading={auditLogQuery.isLoading}
          page={auditPage}
          totalPages={auditLogQuery.data?.pages || 1}
          onPageChange={setAuditPage}
          onGoBack={() => setView("list")}
        />
      )}

      {/* Modals */}
      <DeleteDraftModal
        open={!!deletingAnnouncementId}
        messageTitle={deletingAnnouncementTitle}
        isDeleting={deleteAnnouncementMutation.isLoading}
        onClose={() => {
          setDeletingAnnouncementId(null);
          setDeletingAnnouncementTitle("");
        }}
        onConfirm={() => {
          if (deletingAnnouncementId) {
            deleteAnnouncementMutation.mutate(deletingAnnouncementId, {
              onSuccess: () => {
                setDeletingAnnouncementId(null);
                setDeletingAnnouncementTitle("");
              },
            });
          }
        }}
      />

      <CloseAnnouncementModal
        open={showCloseModal}
        isClosing={closeAnnouncementMutation.isLoading}
        onClose={() => setShowCloseModal(false)}
        onConfirm={() => {
          if (closingAnnouncementId) {
            closeAnnouncementMutation.mutate(closingAnnouncementId, {
              onSuccess: () => {
                setShowCloseModal(false);
                setEditingAnnouncementId(null);
                setViewingAnnouncementId(null);
                setView("list");
              },
            });
          }
        }}
      />
    </PageContainer>
  );
};

export default MultimessagingDirectoryPage;
